import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { isQuantityEditable } from '../utils/lineQuantity';

export type BoardContractPriceApplyInput = {
  line: AssignmentLine;
  supplierId: string;
  costAgreementId: string;
};

/**
 * Builds the replenishment override payload that pins a buy line to a contract
 * price. Returns null when the line cannot accept a contract-priced buy
 * (rejected, no buy action, a substitute plan, missing supplier context).
 *
 * Used by the Unit Cost column and by bulk board edits (#8542).
 */
export function buildBoardContractPriceOverrideInput({
  line,
  supplierId,
  costAgreementId,
}: BoardContractPriceApplyInput): OverrideOptionInput | null {
  const option = line.topOption;
  const buyAction =
    option?.action === 'buy' || option?.action === 'buy_and_transfer'
      ? option.action
      : null;
  if (!isQuantityEditable(line) || buyAction == null) return null;

  const buyQuantity = option?.buyQuantity ?? line.neededQuantity;
  if (buyQuantity <= 0) return null;

  return {
    suggestionId: line.suggestionId,
    action: buyAction,
    supplierId,
    shipToWarehouseId: line.shipToWarehouseId,
    buyQuantity,
    costAgreementId,
    setChosen: true,
  };
}

/**
 * Applies a contract price choice to one board line via the override mutation.
 */
export async function applyBoardContractPriceChoice(
  mutateAsync: (input: OverrideOptionInput) => Promise<unknown>,
  params: BoardContractPriceApplyInput,
): Promise<boolean> {
  const input = buildBoardContractPriceOverrideInput(params);
  if (input == null) return false;
  await mutateAsync(input);
  return true;
}
